<template>
  <div class="dash-root">
    <vue-element-loading
      :active="appLoading"
      spinner="bar-fade-scale"
      color="#3ea56e"
      size="128"
      is-full-screen
    />
    <v-layout v-if="ServerError" wrap justify-center>
      <v-flex px-5 pt-5 xs12 sm12 md12 lg12 xl12 text-center>
        <ServerError />
      </v-flex>
    </v-layout>
    <div v-else>
      <div class="dash-page-header">
        <div>
          <p class="dash-page-header__eyebrow">Reports</p>
          <h1 class="dash-page-header__title">Inventory Report</h1>
        </div>
        <div class="dash-page-header__actions">
          <v-btn class="action-btn-export action-btn-excel" @click="exportExcel" depressed>
            <v-icon left size="20">mdi-file-excel</v-icon> Download Excel
          </v-btn>
          <v-btn class="action-btn-export action-btn-pdf ml-2" @click="exportPdf" depressed>
            <v-icon left size="20">mdi-file-pdf-box</v-icon> Download PDF
          </v-btn>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters-card">
        <v-row align="center">
          <v-col cols="12" sm="6" md="3">
            <v-menu
              v-model="fromDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.fromDate"
                  label="From Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  dense
                  outlined
                  hide-details
                  class="modern-input"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.fromDate"
                @input="fromDateMenu = false"
                no-title
                scrollable
                color="#3ea56e"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-menu
              v-model="toDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.toDate"
                  label="To Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  dense
                  outlined
                  hide-details
                  class="modern-input"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.toDate"
                @input="toDateMenu = false"
                no-title
                scrollable
                color="#3ea56e"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-autocomplete
              v-model="filters.sellerId"
              :items="sellers"
              item-text="name"
              item-value="_id"
              label="Seller"
              dense
              outlined
              hide-details
              clearable
              class="modern-input"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="3" class="d-flex gap-2">
            <v-btn class="btn-filter flex-grow-1" @click="getReport" depressed>Filter</v-btn>
            <v-btn class="btn-reset flex-grow-1" @click="resetFilters" depressed outlined>Reset</v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Summary Cards -->
      <v-row wrap mb-6 v-if="summary">
        <v-col cols="12" sm="6" md="3">
          <v-card class="summary-card summary-card-blue" elevation="0">
            <div class="summary-icon"><v-icon color="#0284c7" size="28">mdi-package-variant-closed</v-icon></div>
            <div class="summary-info">
              <div class="summary-title">Total Products</div>
              <div class="summary-value">{{ summary.totalProducts || 0 }}</div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="summary-card summary-card-green" elevation="0">
            <div class="summary-icon"><v-icon color="#16a34a" size="28">mdi-layers</v-icon></div>
            <div class="summary-info">
              <div class="summary-title">Total Stock</div>
              <div class="summary-value">{{ summary.totalStock || 0 }}</div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="summary-card summary-card-red" elevation="0">
            <div class="summary-icon"><v-icon color="#dc2626" size="28">mdi-alert-circle-outline</v-icon></div>
            <div class="summary-info">
              <div class="summary-title">Out Of Stock</div>
              <div class="summary-value">{{ summary.outOfStockCount || 0 }}</div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="summary-card summary-card-purple" elevation="0">
            <div class="summary-icon"><v-icon color="#9333ea" size="28">mdi-cash-multiple</v-icon></div>
            <div class="summary-info">
              <div class="summary-title">Total Value</div>
              <div class="summary-value">₹{{ summary.totalValue || 0 }}</div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Report Table -->
      <v-card class="modern-card mt-6" elevation="0">
        <v-data-table
          :headers="headers"
          :items="reportData"
          :items-per-page="20"
          class="modern-table"
          :search="search"
        >
          <template v-slot:top>
            <div class="table-header">
              <v-text-field
                v-model="search"
                placeholder="Search inventory..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                hide-details
                class="modern-search-input"
              ></v-text-field>
            </div>
          </template>
          <template v-slot:[`item.seller`]="{ item }">
            <span class="font-weight-medium">{{ item.sellerId ? item.sellerId.firstname + ' ' + (item.sellerId.lastname || '') : 'N/A' }}</span>
          </template>
          <template v-slot:[`item.categories`]="{ item }">
            <v-chip v-if="item.category && item.category.length" small color="#f1f5f9" text-color="#475569" class="font-weight-medium mr-1 mb-1">
              {{ item.category.map(c => c.name).join(', ') }}
            </v-chip>
            <span v-else class="text-muted">N/A</span>
          </template>
          <template v-slot:[`item.subcategories`]="{ item }">
            <span class="text-muted">{{ item.subcategory && item.subcategory.length ? item.subcategory.map(s => s.name).join(', ') : 'N/A' }}</span>
          </template>
          <template v-slot:[`item.price`]="{ item }">
            <span class="price-text">₹{{ item.price }}</span>
          </template>
          <template v-slot:[`item.offerPrice`]="{ item }">
            <span class="price-text font-weight-bold">₹{{ item.offerPrice }}</span>
          </template>
          <template v-slot:[`item.quantity`]="{ item }">
            <v-chip :color="isLowStock(item) ? '#fee2e2' : '#dcfce7'" :text-color="isLowStock(item) ? '#dc2626' : '#16a34a'" small class="font-weight-bold">
              {{ item.quantity }}
            </v-chip>
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <v-chip :color="item.status === 'Active' ? '#dcfce7' : '#f1f5f9'" :text-color="item.status === 'Active' ? '#16a34a' : '#475569'" small class="font-weight-medium">
              {{ item.status }}
            </v-chip>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      search: "",
      fromDateMenu: false,
      toDateMenu: false,
      filters: {
        fromDate: "",
        toDate: "",
        sellerId: null,
      },
      sellers: [],
      reportData: [],
      summary: null,
      headers: [
        { text: "Product Name", value: "productname" },
        { text: "Seller", value: "seller" },
        { text: "Main Category", value: "mainCategory.name" },
        { text: "Category", value: "categories" },
        { text: "Subcategory", value: "subcategories" },
        { text: "Price", value: "price" },
        { text: "Offer Price", value: "offerPrice" },
        { text: "Current Stock", value: "quantity" },
        { text: "Status", value: "status" },
      ],
    };
  },
  mounted() {
    this.getSellers();
    this.getReport();
  },
  methods: {
    getSellers() {
      axios({
        url: "/seller/activeall",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          count: 1000,
          page: 1,
        },
      })
        .then((response) => {
          this.sellers = response.data.data.map((s) => ({
            _id: s._id,
            name: `${s.firstname} ${s.lastname || ""} (${s.organization || "No Org"})`,
          }));
        })
        .catch((err) => {
          console.error("Error fetching sellers:", err);
        });
    },
    getReport() {
      this.appLoading = true;
      axios({
        url: "/admin/report/inventory",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: this.filters,
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.reportData = response.data.inventoryData;
            this.summary = response.data.summary;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error("Error fetching report:", err);
        });
    },
    resetFilters() {
      this.filters = {
        fromDate: "",
        toDate: "",
        sellerId: null,
      };
      this.getReport();
    },
    exportExcel() {
      this.downloadReport("/admin/report/inventory/excel", "inventory_report.xlsx");
    },
    exportPdf() {
      this.downloadReport("/admin/report/inventory/pdf", "inventory_report.pdf");
    },
    downloadReport(url, filename) {
      this.appLoading = true;
      axios({
        url: url,
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: this.filters,
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const blob = new Blob([response.data], { type: response.headers["content-type"] });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        })
        .catch((err) => {
          this.appLoading = false;
          alert("Error downloading report");
          console.error("Download error:", err);
        });
    },
    isLowStock(item) {
      if (item.minimumOrderQuantity) {
        return item.quantity <= item.minimumOrderQuantity * 5;
      } else {
        return item.quantity < 10;
      }
    },
  },
};
</script>

<style scoped>
.dash-root { font-family: poppinsmedium, Roboto, sans-serif; padding: 12px 12px; }
.dash-page-header { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 16px; }
.dash-page-header__eyebrow { font-size: 13px; color: #8a93a4; margin: 0 0 4px; font-family: poppinsmedium, sans-serif; text-transform: uppercase; letter-spacing: 0.5px; }
.dash-page-header__title { font-size: 22px; font-weight: 700; color: #1a202c; margin: 0; font-family: poppinsbold, sans-serif; line-height: 1.2; }
.dash-page-header__actions { display: flex; gap: 12px; }

.filters-card { background: #ffffff; border: 1px solid #eaedf0; border-radius: 12px; padding: 20px; margin-bottom: 24px; box-shadow: 0 2px 8px rgba(0,0,0,0.02) !important; }
.btn-filter { background: #3ea56e !important; color: #ffffff !important; border-radius: 8px; text-transform: none; font-weight: 600; box-shadow: 0 4px 12px rgba(62, 165, 110, 0.2) !important; letter-spacing: 0; height: 40px !important; }
.btn-reset { border-color: #cbd5e1 !important; color: #475569 !important; border-radius: 8px; text-transform: none; font-weight: 600; letter-spacing: 0; background: #ffffff !important; height: 40px !important; }
.action-btn-export { border-radius: 8px; text-transform: none; font-weight: 600; letter-spacing: 0; height: 40px !important; }
.action-btn-excel { background: #dcfce7 !important; color: #16a34a !important; border: 1px solid #bbf7d0 !important; box-shadow: none !important; }
.action-btn-excel:hover { background: #bbf7d0 !important; }
.action-btn-pdf { background: #fee2e2 !important; color: #dc2626 !important; border: 1px solid #fecaca !important; box-shadow: none !important; }
.action-btn-pdf:hover { background: #fecaca !important; }

.summary-card { display: flex; align-items: center; padding: 20px; border-radius: 12px; border: 1px solid transparent; height: 100%; transition: all 0.2s ease; }
.summary-card:hover { transform: translateY(-2px); }
.summary-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-right: 16px; background: rgba(255,255,255,0.6); }
.summary-info { display: flex; flex-direction: column; }
.summary-title { font-size: 13px; color: #64748b; font-family: poppinsmedium, sans-serif; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.summary-value { font-size: 24px; font-weight: 700; color: #0f172a; font-family: poppinsbold, sans-serif; line-height: 1.2; }

.summary-card-blue { background: #e0f2fe; border-color: #bae6fd; }
.summary-card-green { background: #dcfce7; border-color: #bbf7d0; }
.summary-card-red { background: #fee2e2; border-color: #fecaca; }
.summary-card-purple { background: #f3e8ff; border-color: #e9d5ff; }

.modern-card { background: #ffffff; border: 1px solid #eaedf0; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02) !important; }
.table-header { padding: 16px 20px; border-bottom: 1px solid #eaedf0; background: #ffffff; display: flex; align-items: center; justify-content: space-between; }
.modern-search-input { max-width: 300px; }
.modern-search-input ::v-deep .v-input__control .v-input__slot { border-radius: 8px; min-height: 40px !important; }
.gap-2 { gap: 8px; }

::v-deep .modern-table .v-data-table-header th { font-family: poppinssemibold, sans-serif; font-size: 13px !important; color: #64748b !important; text-transform: uppercase; letter-spacing: 0.5px; background-color: #f8fafc !important; border-bottom: 1px solid #eaedf0 !important; padding: 12px 16px !important; white-space: nowrap !important; vertical-align: middle !important; }
::v-deep .modern-table .v-data-table-header th span { vertical-align: middle; display: inline-flex; align-items: center; }
::v-deep .modern-table tbody td { font-family: poppinsmedium, sans-serif; font-size: 14px; color: #1a202c; border-bottom: 1px solid #eaedf0 !important; padding: 12px 16px !important; }
::v-deep .modern-table tbody tr:hover { background-color: #f8fafc !important; }
.price-text { font-family: 'Courier New', Courier, monospace; letter-spacing: 0.5px; font-weight: 600; color: #334155; }
.text-muted { color: #94a3b8; }
</style>
